import React from 'react';
import { Link, useLoaderData, useLocation, useOutlet, useRevalidator } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { BoltIcon, ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Await } from '@oysterjs/ui/async';
import { Button } from '@oysterjs/ui/button';
import { type ColumnDef, DataTable, type SortDirection } from '@oysterjs/ui/data-table';
import {
  type ActiveFilter,
  SearchFilterInput,
  useFiltersFromLocalStorage
} from '@oysterjs/ui/search-filter';

import {
  type CustomPresetFieldsFragment,
  TriggerDefinitionStatus,
  type TriggerDefinitionFieldsFragment
} from '~/api/graphql';
import { useOptionalCurrentAgencyUser } from '~/components/current-user';
import { FormError } from '~/components/form-error';
import { ProLabel } from '~/components/pro-label';
import { RowActionsMenu } from '~/components/row-actions-menu';
import { UsageAllotmentBanner } from '~/components/usage-allotment-banner';
import { type ViewMode, ViewModeToggle, usePersistedViewMode } from '~/components/view-mode-toggle';
import { LibraryListEmptyState } from '~/components/list/library-list-empty-state';
import { LibraryListLoading } from '~/components/list/library-list-loading';
import { UpdatedAtCell } from '~/components/list/library-table-cells';
import { TriggersProUpgradeDialog } from './triggers-upgrade';
import { useTriggersEntitlement } from './use-triggers-entitlement';
import {
  createTriggerFilterDefinitions,
  filterTriggers,
  type TriggerFilter
} from './trigger-filters';
import { DeleteTriggerDialog, useTriggerMutations } from './trigger-actions';
import {
  agencyEmailNamespace,
  compareText,
  EVENT_TYPE_LABELS,
  EventTypeBadge,
  sortRows,
  triggerEditHref,
  triggerHref,
  triggerSourceDescription,
  TriggerStatusBadge,
  TriggerTaskCell,
  triggerTaskSearchText,
  triggerTaskSummary,
  type TriggerTaskSummary,
  namesMailboxAccounts,
  useMailboxLabel
} from './trigger-utils';

type TriggersLoaderData = {
  triggerDefinitions?: TriggerDefinitionFieldsFragment[];
  customPresets?: CustomPresetFieldsFragment[];
};

const rowHref = (trigger: TriggerDefinitionFieldsFragment) => triggerHref(trigger.id);

type TriggerActions = {
  canManage: boolean;
  onUpgrade: () => void;
  onPauseResume: (trigger: TriggerDefinitionFieldsFragment) => void;
  onDelete: (trigger: TriggerDefinitionFieldsFragment) => void;
};

const TriggerActionsMenu = ({
  trigger,
  canManage,
  onUpgrade,
  onPauseResume,
  onDelete
}: TriggerActions & { trigger: TriggerDefinitionFieldsFragment }) => (
  <RowActionsMenu
    label={`Open options for ${trigger.name}`}
    actions={[
      canManage
        ? { label: 'Edit', href: triggerEditHref(trigger.id) }
        : { label: 'Edit', onSelect: onUpgrade },
      {
        label: trigger.status === TriggerDefinitionStatus.Active ? 'Pause' : 'Resume',
        onSelect: canManage ? () => onPauseResume(trigger) : onUpgrade
      },
      { label: 'Delete', onSelect: canManage ? () => onDelete(trigger) : onUpgrade }
    ]}
  />
);

const TriggerSourceText = ({
  description,
  className = ''
}: {
  description: string;
  className?: string;
}) => (
  <span
    className={`block truncate text-sm text-neutral-700 dark:text-neutral-300 ${className}`}
    title={description}
  >
    {description}
  </span>
);

const TriggerCard = ({
  trigger,
  sourceDescription,
  task
}: {
  trigger: TriggerDefinitionFieldsFragment;
  sourceDescription: string;
  task: TriggerTaskSummary;
}) => {
  return (
    <div className="h-full w-full rounded-lg bg-white dark:bg-neutral-800 shadow hover:shadow-xl transition-all duration-300">
      <Link to={rowHref(trigger)} className="flex h-full w-full flex-col p-6 text-left">
        <div className="flex-1">
          <h3 className="text-base/6 font-body m-0 p-0 font-medium truncate" title={trigger.name}>
            {trigger.name}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <TriggerStatusBadge status={trigger.status} />
            <EventTypeBadge eventType={trigger.eventMatcher.eventType} />
          </div>
          <TriggerSourceText description={sourceDescription} className="mt-3" />
          <div className="mt-3">
            <TriggerTaskCell task={task} />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-neutral-100 dark:border-neutral-700 pt-4">
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            Updated{' '}
            {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
              new Date(trigger.updatedAt)
            )}
          </span>
          <ChevronRightIcon className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
        </div>
      </Link>
    </div>
  );
};

type TriggerSortKey = 'name' | 'status' | 'type' | 'source' | 'task' | 'date';

const sortTriggers = (
  triggers: TriggerDefinitionFieldsFragment[],
  key: TriggerSortKey,
  direction: SortDirection,
  sourceDescription: (trigger: TriggerDefinitionFieldsFragment) => string,
  taskSummary: (trigger: TriggerDefinitionFieldsFragment) => TriggerTaskSummary
): TriggerDefinitionFieldsFragment[] => {
  const compare = (a: TriggerDefinitionFieldsFragment, b: TriggerDefinitionFieldsFragment) => {
    switch (key) {
      case 'name':
        return compareText(a.name, b.name);
      case 'status':
        return a.status.localeCompare(b.status);
      case 'type':
        return compareText(
          EVENT_TYPE_LABELS[a.eventMatcher.eventType],
          EVENT_TYPE_LABELS[b.eventMatcher.eventType]
        );
      case 'source':
        return compareText(sourceDescription(a), sourceDescription(b));
      case 'task':
        return compareText(
          triggerTaskSearchText(taskSummary(a)),
          triggerTaskSearchText(taskSummary(b))
        );
      case 'date':
        return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    }
  };
  return sortRows(triggers, compare, direction);
};

const getTriggerColumns = (
  sourceDescription: (trigger: TriggerDefinitionFieldsFragment) => string,
  taskSummary: (trigger: TriggerDefinitionFieldsFragment) => TriggerTaskSummary,
  actions: TriggerActions
): ColumnDef<TriggerDefinitionFieldsFragment, TriggerSortKey>[] => [
  {
    id: 'name',
    header: 'Name',
    sortKey: 'name',
    renderCell: (trigger) => (
      <span
        className="block truncate text-sm font-medium text-neutral-900 dark:text-white"
        title={trigger.name}
      >
        {trigger.name}
      </span>
    )
  },
  {
    id: 'status',
    header: 'Status',
    sortKey: 'status',
    renderCell: (trigger) => <TriggerStatusBadge status={trigger.status} />
  },
  {
    id: 'type',
    header: 'Type',
    sortKey: 'type',
    renderCell: (trigger) => <EventTypeBadge eventType={trigger.eventMatcher.eventType} />
  },
  {
    id: 'source',
    header: 'Source',
    sortKey: 'source',
    headerClassName: 'hidden md:table-cell',
    cellClassName: 'hidden md:table-cell max-w-xs',
    renderCell: (trigger) => <TriggerSourceText description={sourceDescription(trigger)} />
  },
  {
    id: 'task',
    header: 'Task',
    sortKey: 'task',
    headerClassName: 'hidden lg:table-cell',
    cellClassName: 'hidden lg:table-cell max-w-xs',
    renderCell: (trigger) => <TriggerTaskCell task={taskSummary(trigger)} />
  },
  {
    id: 'date',
    header: 'Last Updated',
    sortKey: 'date',
    headerClassName: 'hidden sm:table-cell',
    cellClassName: 'hidden sm:table-cell',
    renderCell: (trigger) => <UpdatedAtCell value={trigger.updatedAt} />
  },
  {
    id: 'actions',
    header: '',
    noHref: true,
    cellClassName: 'w-0 py-1.5 px-3',
    renderCell: (trigger) => <TriggerActionsMenu trigger={trigger} {...actions} />
  }
];

const TriggerCollection = ({
  triggers,
  customPresets,
  emailNamespace,
  viewMode,
  activeFilters,
  sortBy,
  sortDirection,
  onSort,
  actions
}: {
  triggers: TriggerDefinitionFieldsFragment[];
  customPresets: CustomPresetFieldsFragment[];
  emailNamespace: string;
  viewMode: ViewMode;
  activeFilters: ActiveFilter<TriggerFilter>[];
  sortBy?: TriggerSortKey;
  sortDirection: SortDirection;
  onSort: (key: TriggerSortKey, direction: SortDirection) => void;
  actions: TriggerActions;
}) => {
  const mailboxLabel = useMailboxLabel(!triggers.some(namesMailboxAccounts));
  const sourceDescription = React.useCallback(
    (trigger: TriggerDefinitionFieldsFragment) =>
      triggerSourceDescription(trigger, emailNamespace, mailboxLabel),
    [emailNamespace, mailboxLabel]
  );
  const presetTitles = React.useMemo(
    () => new Map(customPresets.map((preset) => [preset.id, preset.title])),
    [customPresets]
  );
  const taskSummary = React.useCallback(
    (trigger: TriggerDefinitionFieldsFragment) => triggerTaskSummary(trigger.target, presetTitles),
    [presetTitles]
  );
  const filteredTriggers = React.useMemo(
    () =>
      filterTriggers(triggers, activeFilters, (trigger) =>
        [
          trigger.name,
          sourceDescription(trigger),
          triggerTaskSearchText(taskSummary(trigger))
        ].join(' ')
      ),
    [triggers, activeFilters, sourceDescription, taskSummary]
  );
  const sortFn = React.useCallback(
    (rows: TriggerDefinitionFieldsFragment[], key: TriggerSortKey, direction: SortDirection) =>
      sortTriggers(rows, key, direction, sourceDescription, taskSummary),
    [sourceDescription, taskSummary]
  );

  if (triggers.length === 0) {
    return (
      <LibraryListEmptyState
        icon={BoltIcon}
        title="No triggers yet"
        description={
          actions.canManage
            ? 'Create a trigger to launch a preset workspace when an email arrives, a schedule comes due, or a call ends.'
            : 'Upgrade to Pro to create email, schedule, and call-completion triggers.'
        }
      />
    );
  }

  if (filteredTriggers.length === 0) {
    return (
      <LibraryListEmptyState
        icon={MagnifyingGlassIcon}
        title="No matching triggers"
        description="Try adjusting your search or filters."
      />
    );
  }

  if (viewMode === 'card') {
    return (
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {filteredTriggers.map((trigger) => (
          <TriggerCard
            key={trigger.id}
            trigger={trigger}
            sourceDescription={sourceDescription(trigger)}
            task={taskSummary(trigger)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 rounded-lg bg-white shadow dark:bg-neutral-800">
      <DataTable
        data={filteredTriggers}
        columns={getTriggerColumns(sourceDescription, taskSummary, actions)}
        keyExtractor={(trigger) => trigger.id}
        rowHref={rowHref}
        tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
        sortKey={sortBy}
        sortDirection={sortDirection}
        onSort={onSort}
        sortFn={sortFn}
      />
    </div>
  );
};

export const WorkflowTriggersPage = () => {
  const canManageTriggers = useTriggersEntitlement();
  const agencyUser = useOptionalCurrentAgencyUser();
  const emailNamespace = agencyEmailNamespace(agencyUser?.agency);
  const location = useLocation();
  const outlet = useOutlet();
  const revalidator = useRevalidator();
  const loader = useLoaderData() as { data?: Promise<TriggersLoaderData> } | undefined;
  const [viewMode, setViewMode] = usePersistedViewMode('cara.triggers.view');
  const [showProUpgradeDialog, setShowProUpgradeDialog] = React.useState(false);
  const [deleting, setDeleting] = React.useState<TriggerDefinitionFieldsFragment | null>(null);
  const [statusError, setStatusError] = React.useState<string | null>(null);
  const { toggleStatus } = useTriggerMutations();
  const filterDefinitions = React.useMemo(() => createTriggerFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, initialFilters, sortBy, sortDirection, setSort } =
    useFiltersFromLocalStorage<TriggerFilter, TriggerSortKey>(
      'workflow_triggers_default_view',
      filterDefinitions,
      { defaultSortBy: 'date', defaultSortDirection: 'desc' }
    );
  const pauseOrResume = async (trigger: TriggerDefinitionFieldsFragment) => {
    setStatusError(null);
    const message = await toggleStatus(trigger);
    setStatusError(message);
    if (!message) revalidator.revalidate();
  };

  const createAction = canManageTriggers ? (
    <Button color="sky" href="/app/workflows/triggers/new">
      Create trigger
    </Button>
  ) : (
    <Button color="sky" onClick={() => setShowProUpgradeDialog(true)}>
      Upgrade to Pro
    </Button>
  );

  const actions: TriggerActions = {
    canManage: canManageTriggers,
    onUpgrade: () => setShowProUpgradeDialog(true),
    onPauseResume: (trigger) => void pauseOrResume(trigger),
    onDelete: setDeleting
  };

  return (
    <>
      <div className="space-y-4">
        <UsageAllotmentBanner feature="triggers" />

        <div className="space-y-2">
          <SearchFilterInput
            filterDefinitions={filterDefinitions}
            onFiltersChange={setActiveFilters}
            initialFilters={initialFilters}
            placeholder="Filter by status, type, or search..."
          />
          <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <ViewModeToggle value={viewMode} onChange={setViewMode} />
              {!canManageTriggers && (
                <ProLabel tooltip="Triggers are available on the Pro plan" variant="emphasis" />
              )}
            </div>
            {createAction}
          </div>
        </div>

        <FormError message={statusError} className="text-sm text-red-600 dark:text-red-400" />

        <React.Suspense fallback={<LibraryListLoading title="Loading triggers..." />}>
          <Await resolve={loader?.data}>
            {(data: TriggersLoaderData) => (
              <TriggerCollection
                triggers={data.triggerDefinitions ?? []}
                customPresets={data.customPresets ?? []}
                emailNamespace={emailNamespace}
                viewMode={viewMode}
                activeFilters={activeFilters}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={setSort}
                actions={actions}
              />
            )}
          </Await>
        </React.Suspense>
      </div>

      <DeleteTriggerDialog
        trigger={deleting}
        emailNamespace={emailNamespace}
        onClose={() => setDeleting(null)}
        onDeleted={() => {
          setDeleting(null);
          revalidator.revalidate();
        }}
      />

      <TriggersProUpgradeDialog
        open={showProUpgradeDialog}
        onClose={() => setShowProUpgradeDialog(false)}
      />

      <AnimatePresence mode="wait">
        {outlet && React.cloneElement(outlet, { key: location.pathname })}
      </AnimatePresence>
    </>
  );
};
